import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RiskCard } from '@/entities/risk/model/riskSchema';
import type { RiskMeasure } from '@/entities/risk/model/riskSchema';
import { mockDelay } from '@/shared/api/mockDelay';
import { appendJsonArray, readJsonArray } from '@/shared/api/queryStorage';
import { buildRiskCardDetail, riskCards, riskMeasures } from './mockData';

const RISK_CARDS_EXTRA_KEY = 'ohs-arm-risk-cards-extra';
const RISK_CARDS_PATCH_KEY = 'ohs-arm-risk-cards-patch';
const RISK_MEASURES_EXTRA_KEY = 'ohs-arm-risk-measures-extra';
const RISK_HISTORY_KEY = 'ohs-arm-risk-history';

function mergeRiskCardsFromStorage(): RiskCard[] {
  const extras = readJsonArray<RiskCard>(RISK_CARDS_EXTRA_KEY);
  const patches = readJsonArray<Partial<RiskCard> & { id: number }>(RISK_CARDS_PATCH_KEY);
  const patchMap = new Map(patches.map((p) => [p.id, p]));
  const base = [...riskCards, ...extras];
  return base.map((r) => {
    const p = patchMap.get(r.id);
    return p ? { ...r, ...p } : r;
  });
}

/** GET /api/risks/cards */
export function useRiskCards() {
  return useQuery({
    queryKey: ['risks', 'cards'],
    queryFn: () => mockDelay(mergeRiskCardsFromStorage()),
  });
}

/** GET /api/risks/cards/:id */
export function useRiskCard(id: number) {
  return useQuery({
    queryKey: ['risks', 'cards', 'detail', id],
    queryFn: async () => {
      const merged = mergeRiskCardsFromStorage();
      const card = merged.find((r) => r.id === id);
      if (!card) return null;
      return mockDelay(buildRiskCardDetail(card));
    },
    enabled: Number.isFinite(id) && id > 0,
  });
}

/** POST /api/risks/cards */
export function useAddRiskCard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Omit<RiskCard, 'id'>) => {
      const id = Date.now();
      const row: RiskCard = { id, ...input };
      appendJsonArray(RISK_CARDS_EXTRA_KEY, row);
      appendJsonArray(RISK_HISTORY_KEY, {
        date: new Date().toLocaleString('ru-RU'),
        action: `Создана карта: ${row.workplace} — ${row.hazard} (балл ${row.riskScore})`,
      });
      await mockDelay(null);
      return row;
    },
    onSuccess: (row) => {
      void queryClient.invalidateQueries({ queryKey: ['risks'] });
      void queryClient.invalidateQueries({ queryKey: ['risks', 'cards', 'detail', row.id] });
    },
  });
}

/** PATCH /api/risks/cards/:id */
export function useUpdateRiskCard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<RiskCard> & { id: number }) => {
      const patches = readJsonArray<Partial<RiskCard> & { id: number }>(RISK_CARDS_PATCH_KEY);
      const idx = patches.findIndex((p) => p.id === patch.id);
      if (idx >= 0) patches[idx] = { ...patches[idx], ...patch };
      else patches.push(patch);
      localStorage.setItem(RISK_CARDS_PATCH_KEY, JSON.stringify(patches));
      appendJsonArray(RISK_HISTORY_KEY, {
        date: new Date().toLocaleString('ru-RU'),
        action: `Изменена карта id=${patch.id}: ${Object.keys(patch)
          .filter((k) => k !== 'id')
          .join(', ')}`,
      });
      await mockDelay(null);
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['risks'] });
      void queryClient.invalidateQueries({ queryKey: ['risks', 'cards', 'detail', variables.id] });
    },
  });
}

/** GET /api/risks/measures */
export function useRiskMeasures() {
  return useQuery({
    queryKey: ['risks', 'measures'],
    queryFn: () => mockDelay([...riskMeasures, ...readJsonArray<RiskMeasure>(RISK_MEASURES_EXTRA_KEY)]),
  });
}

/** POST /api/risks/measures */
export function useAddRiskMeasure() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Omit<RiskMeasure, 'id'>) => {
      const id = Date.now();
      const row: RiskMeasure = { id, ...input };
      appendJsonArray(RISK_MEASURES_EXTRA_KEY, row);
      appendJsonArray(RISK_HISTORY_KEY, {
        date: new Date().toLocaleString('ru-RU'),
        action: `Добавлено мероприятие: ${row.title} (${row.workplaceId})`,
      });
      await mockDelay(null);
      return row;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['risks'] });
    },
  });
}

/** GET /api/risks/history */
export function useRiskHistory() {
  return useQuery({
    queryKey: ['risks', 'history'],
    queryFn: () => mockDelay(readJsonArray<{ date: string; action: string }>(RISK_HISTORY_KEY)),
  });
}
